import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { TextWithTooltip } from 'components/WithTooltip';
import type { ContractStatusEnum, WorkOrderPriceItem, WorkOrderPriceSeasonValue } from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import { WorkOrderPriceVolumeEdit } from '../../WorkOrderPriceVolumeEdit';

import Styles from './cellRenderer.module.css';

interface ISeasonVolumeRenderer extends ICellRendererParams<WorkOrderPriceItem> {
    seasonId: number;
    valueKey: keyof WorkOrderPriceSeasonValue;
    statusContract: ContractStatusEnum | undefined;
    disabledByProjectStatus: boolean;
    toolTipValueKey?: keyof WorkOrderPriceSeasonValue;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
}

const requestParams = { isSMRItem: true, isRootObject: true };

export const SeasonVolumeRenderer = (props: ISeasonVolumeRenderer) => {
    const {
        value,

        data,
        seasonId,
        valueKey,
        workOrderStatus,
        disabledByProjectStatus,
        statusContract,
        toolTipValueKey,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        api,
    } = props;

    const { order: orderRules } = useAccess({});

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            api,
            requestParams: { ...useAgCellRowDataRefreshParams, ...requestParams },
        }) || {};

    const onChange = useCallback(
        (reloadRowsIds?: number[] | null) => {
            reloadHandler?.([...(reloadRowsIds ?? []), data?.rootObjectId]);
        },
        [data?.rootObjectId, reloadHandler],
    );

    if (
        !data ||
        data?.type === WorkOrderPriceTypeEnum.RootObject ||
        data?.type === WorkOrderPriceTypeEnum.Other ||
        data?.type === WorkOrderPriceTypeEnum.OtherGroup
    ) {
        return '';
    }

    const currentSeasonData = data?.seasonValues?.find((season) => season.seasonId === seasonId);
    const currentSeasonDataValue = currentSeasonData?.[valueKey] === 0 ? '0' : currentSeasonData?.[valueKey];
    const priceId = currentSeasonData?.priceId;
    const toolTipValue = toolTipValueKey && currentSeasonData?.[toolTipValueKey];

    const tooltipMessage =
        toolTipValue?.length > 1
            ? toolTipValue.reduce((acc: string, p: number, idx: number) => `${acc} ${idx ? '+' : ''} ${p}`, '')
            : undefined;

    const isDisabled = data.isExcludedPrice || disabledByProjectStatus || !orderRules?.canEdit;

    if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
        return (
            <WorkOrderPriceVolumeEdit
                workOrderStatus={workOrderStatus}
                workOrderSeasonId={seasonId}
                workOrderPriceId={priceId}
                defaultValue={currentSeasonDataValue}
                onChange={onChange}
                type={data?.type}
                contractStatus={statusContract}
                toolTipValue={tooltipMessage}
                isDisabled={isDisabled}
            />
        );
    }

    return (
        <div className={Styles.seasonValueTooltip}>
            <TextWithTooltip tooltipText={tooltipMessage} text={value} />
        </div>
    );
};

SeasonVolumeRenderer.displayName = 'SeasonVolumeRenderer';
